import { Link } from "react-router-dom";
import { formatDateHuman, type InvoiceListRow } from "@themislex/shared";
import { EmptyState, InvoiceStatusBadge, Money } from "./ui";

/**
 * Invoice list used by both roles. The lawyer's variant shows the client
 * column; the client's list is already scoped server-side.
 */
export function InvoiceTable({
  invoices,
  showClient = false,
  emptyHint,
  lawyerView = false,
}: {
  invoices: InvoiceListRow[];
  showClient?: boolean;
  emptyHint?: string;
  lawyerView?: boolean;
}) {
  if (invoices.length === 0) {
    return (
      <EmptyState
        title="No invoices on record."
        hint={emptyHint ?? "Invoices raised for this selection will appear here."}
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">Invoice</th>
            {showClient && <th scope="col">Client</th>}
            <th scope="col">Matter</th>
            <th scope="col">Issued</th>
            <th scope="col">Due</th>
            <th scope="col" className="!text-right">
              Amount
            </th>
            <th scope="col" className="!text-right">
              Outstanding
            </th>
            <th scope="col">Status</th>
            <th scope="col">
              <span className="sr-only">Download</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice) => (
            <tr key={invoice.id}>
              <td className="font-medium">
                {lawyerView ? (
                  <Link
                    to={`/desk/invoices/${invoice.id}`}
                    className="text-accent-text hover:underline"
                  >
                    {invoice.invoiceNumber}
                  </Link>
                ) : (
                  invoice.invoiceNumber
                )}
              </td>
              {showClient && <td>{invoice.clientName}</td>}
              <td className="max-w-[220px] truncate" title={invoice.matterTitle}>
                {invoice.matterTitle}
              </td>
              <td className="whitespace-nowrap">{formatDateHuman(invoice.issuedDate)}</td>
              <td className="whitespace-nowrap">{formatDateHuman(invoice.dueDate)}</td>
              <td className="whitespace-nowrap text-right">
                <Money minor={invoice.amountMinor} />
              </td>
              <td className="whitespace-nowrap text-right font-medium">
                {invoice.displayStatus === "paid" || invoice.displayStatus === "draft" ? (
                  "—"
                ) : (
                  <Money minor={invoice.outstandingMinor} />
                )}
              </td>
              <td>
                <InvoiceStatusBadge status={invoice.displayStatus} />
              </td>
              <td className="text-right">
                <a
                  className="btn btn-ghost px-2 py-1 text-xs"
                  href={`/api/invoices/${invoice.id}/pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  PDF
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
